/**
 * pages/anniversary-setup.tsx
 *
 * Anniversary Setup screen — step 3 of onboarding.
 * User can optionally set the couple's anniversary date.
 * On success (or skip) → /home
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUpdateCouple } from '../hooks/use-couple'

const StepDots = ({ current, total }: { current: number; total: number }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`rounded-full transition-all duration-300 ${
          i + 1 === current
            ? 'w-6 h-2 bg-[#D4537E]'
            : 'w-2 h-2 bg-[#F4C0D1]'
        }`}
      />
    ))}
  </div>
)

export default function AnniversarySetupPage() {
  const navigate = useNavigate()
  const { mutate: updateCouple, isPending, error } = useUpdateCouple()

  const [date, setDate] = useState('')
  const [dateError, setDateError] = useState('')

  const handleSetDate = () => {
    if (!date) {
      setDateError('Please pick a date')
      return
    }
    const parsed = new Date(date)
    if (Number.isNaN(parsed.getTime())) {
      setDateError('Invalid date')
      return
    }
    if (parsed > new Date()) {
      setDateError("Anniversary date can't be in the future")
      return
    }
    setDateError('')
    updateCouple(
      { anniversary_date: date },
      { onSuccess: () => navigate('/home') },
    )
  }

  const handleSkip = () => navigate('/home')

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] px-5 pt-[env(safe-area-inset-top)] pb-2">
        <div className="flex items-center gap-2 h-12">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-[#D4537E] font-['DM_Sans'] text-[15px] active:opacity-60 transition-opacity duration-100 touch-manipulation -ml-1"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-5 pb-[88px] overflow-y-auto">
        <div className="animate-fade-in-up">
          <StepDots current={3} total={3} />

          {/* Title */}
          <div className="mb-8 [animation-delay:60ms] animate-fade-in-up">
            <h1 className="font-['Playfair_Display'] text-[28px] font-semibold text-[#2C1810] leading-tight mb-2">
              When did it start?
            </h1>
            <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62]">
              Set your anniversary date and we'll count every day together.
            </p>
          </div>

          {/* Illustration */}
          <div className="flex justify-center mb-8 [animation-delay:90ms] animate-fade-in-up">
            <div className="w-[80px] h-[80px] rounded-full bg-[#FBEAF0] border-2 border-[#F4C0D1] flex items-center justify-center text-[40px] shadow-[0_4px_16px_rgba(212,83,126,0.14)]">
              📅
            </div>
          </div>

          {/* Date Input */}
          <div className="flex flex-col gap-3 [animation-delay:120ms] animate-fade-in-up">
            <div>
              <label className="block font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-1.5">
                Anniversary date
              </label>
              <input
                type="date"
                value={date}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  setDate(e.target.value)
                  setDateError('')
                }}
                className={`
                  w-full h-[52px] px-4
                  bg-[#FAF8F4] font-['DM_Sans'] text-[15px]
                  rounded-[12px]
                  border-[1.5px] outline-none
                  transition-all duration-150
                  focus:border-[#D4537E] focus:shadow-[0_0_0_3px_rgba(212,83,126,0.12)]
                  ${date ? 'text-[#D4537E]' : 'text-[#B89AA2]'}
                  ${dateError ? 'border-[#C0392B]' : 'border-[#F4C0D1]'}
                `}
              />
              {dateError && (
                <p className="mt-1 font-['DM_Sans'] text-[12px] text-[#C0392B]">{dateError}</p>
              )}
            </div>

            {/* API error */}
            {error && (
              <p className="font-['DM_Sans'] text-[13px] text-[#C0392B] text-center">
                {error.message}
              </p>
            )}

            {/* Set date button */}
            <button
              type="button"
              onClick={handleSetDate}
              disabled={isPending}
              className="
                w-full h-[52px] px-6 mt-2
                bg-[#D4537E] text-white
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px]
                shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                transition-all duration-150 ease-out
                active:bg-[#993556] active:scale-[0.98] active:shadow-none
                disabled:opacity-40 disabled:cursor-not-allowed
                select-none touch-manipulation
              "
            >
              {isPending ? '💕 Saving…' : 'Set our date'}
            </button>

            {/* Skip */}
            <button
              type="button"
              onClick={handleSkip}
              disabled={isPending}
              className="
                w-full h-[52px] px-6
                bg-transparent text-[#7A5A62]
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px] border border-[#F4C0D1]
                transition-all duration-150 ease-out
                active:bg-[#FBEAF0]
                disabled:opacity-40
                select-none touch-manipulation
              "
            >
              Skip for now
            </button>
          </div>

          {/* Hint */}
          <div className="mt-8 [animation-delay:180ms] animate-fade-in-up">
            <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2] text-center">
              You can always update this later in settings.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
